import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, CreditCard, CheckCircle, AlertCircle, Camera } from "lucide-react";
import { AiAvatar } from "../../components/ui/ai-avatar";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";

const IDLE_TIMEOUT = 120; // seconds

export default function KioskBlueBadgeIntro() {
  const navigate = useNavigate();
  const location = useLocation();
  const registrationData = location.state?.registrationData;
  const idleTimerRef = useRef(null);
  
  const [animationStep, setAnimationStep] = useState(0);

  // Idle detection
  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => navigate('/kiosk/attract'), IDLE_TIMEOUT * 1000);
    };
    const events = ['mousedown', 'touchstart', 'keydown'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [navigate]);

  // Animation cycle for badge insertion visual
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationStep(prev => (prev + 1) % 4);
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!registrationData) {
      navigate("/kiosk/category");
    }
  }, [registrationData, navigate]);

  if (!registrationData) {
    return null;
  }

  const handleContinue = () => {
    navigate("/kiosk/badge-scan", { state: { registrationData } });
  };

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ background: 'linear-gradient(180deg, #F8FAFC 0%, #EFF6FF 50%, #F0FDF4 100%)' }}
      data-testid="kiosk-bluebadge-intro"
    >
      {/* Header */}
      <header className="flex items-center justify-between p-6 md:p-8">
        <ParkWiseLogo size="lg" />
        
        {/* Progress */}
        <div className="flex items-center gap-2">
          <div className="progress-step completed">
            <CheckCircle className="w-5 h-5" />
          </div>
          <div className="progress-line completed w-8"></div>
          <div className="progress-step completed">
            <CheckCircle className="w-5 h-5" />
          </div>
          <div className="progress-line completed w-8"></div>
          <div className="progress-step active">3</div>
          <div className="progress-line w-8"></div>
          <div className="progress-step pending">4</div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col px-6 md:px-12 pb-8">
        {/* AI Avatar */}
        <div className="flex justify-center mb-6">
          <AiAvatar 
            message="Please insert your Blue Badge into the reader on the side of the kiosk. Place the badge flat into the small letterbox within the reader box. The cameras above and below the badge will take pictures automatically." 
            size="medium"
            autoSpeak={true}
          />
        </div>

        {/* Title */}
        <div className="text-center mb-6">
          <h1 
            className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Insert Your Blue Badge
          </h1>
          <p className="text-lg text-slate-600 max-w-xl mx-auto">
            The reader will automatically scan both sides of your badge
          </p>
        </div>

        {/* Badge Reader Illustration - LEFT SIDE */}
        <div className="flex-1 flex items-center justify-center">
          <div className="relative max-w-2xl w-full flex items-center gap-12">
            
            {/* Side badge reader on the LEFT */}
            <div className="flex-shrink-0">
              <div className={`
                bg-slate-700 rounded-xl p-4 border-4 transition-all duration-500 shadow-xl
                ${animationStep >= 2 ? 'border-green-400 shadow-green-400/30' : 'border-blue-400 shadow-blue-400/30'}
              `}>
                {/* Reader housing */}
                <div className="w-28 h-44 bg-slate-600 rounded-lg relative overflow-hidden">
                  {/* Letterbox slot - horizontal insertion */}
                  <div className="absolute top-1/2 -translate-y-1/2 left-2 right-2 h-14 bg-slate-800 rounded-lg border-2 border-slate-500 flex items-center justify-center">
                    {/* Badge insertion animation */}
                    <div 
                      className={`
                        bg-blue-600 rounded transition-all duration-700 ease-in-out flex items-center justify-center
                        ${animationStep === 0 ? 'w-0 h-10 opacity-0' : 
                          animationStep === 1 ? 'w-16 h-10 opacity-100' :
                          animationStep === 2 ? 'w-20 h-10 opacity-100' : 
                          'w-0 h-10 opacity-0'}
                      `}
                    >
                      {animationStep >= 1 && animationStep <= 2 && (
                        <span className="text-white text-xs font-bold">BADGE</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Top camera indicator */}
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 flex flex-col items-center">
                    <Camera className={`w-5 h-5 ${animationStep === 2 ? 'text-green-400' : 'text-slate-400'}`} />
                    <span className="text-xs text-slate-400 mt-1">Top Cam</span>
                  </div>
                  
                  {/* Bottom camera indicator */}
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex flex-col items-center">
                    <span className="text-xs text-slate-400 mb-1">Bottom Cam</span>
                    <Camera className={`w-5 h-5 ${animationStep === 2 ? 'text-green-400' : 'text-slate-400'}`} />
                  </div>
                  
                  {/* Reader light */}
                  <div className={`
                    absolute bottom-10 right-3 w-2 h-2 rounded-full transition-colors duration-300
                    ${animationStep >= 2 ? 'bg-green-400' : 'bg-blue-400 animate-pulse'}
                  `}></div>
                </div>
                
                {/* Label */}
                <p className="text-xs text-slate-300 text-center mt-3 font-medium">
                  Badge Reader
                </p>
              </div>
              
              {/* Arrow indicator pointing to reader */}
              <div className={`
                flex justify-center mt-4 transition-opacity duration-500
                ${animationStep <= 1 ? 'opacity-100' : 'opacity-0'}
              `}>
                <div className="flex items-center gap-2 text-blue-500 animate-pulse">
                  <span className="text-sm font-medium">Insert Here</span>
                  <ArrowLeft className="w-5 h-5 rotate-90" />
                </div>
              </div>
            </div>

            {/* Kiosk representation on RIGHT */}
            <div className="bg-slate-800 rounded-3xl p-6 shadow-2xl flex-1">
              {/* Screen area */}
              <div className="bg-gradient-to-br from-blue-500 to-green-400 rounded-2xl h-56 flex items-center justify-center">
                <div className="text-white text-center">
                  <CreditCard className="w-12 h-12 mx-auto mb-2 opacity-80" />
                  <p className="text-lg font-medium">Kiosk Screen</p>
                  <p className="text-sm opacity-80 mt-1">You are here</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Status text */}
        <div className="mt-4 text-center">
          <div className={`
            inline-flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-500
            ${animationStep >= 2 
              ? 'bg-green-100 text-green-700' 
              : 'bg-blue-100 text-blue-700'}
          `}>
            {animationStep >= 2 ? (
              <>
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Badge Detected - Cameras Ready</span>
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5 animate-pulse" />
                <span className="font-medium">Insert Badge into Letterbox Slot</span>
              </>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="max-w-2xl mx-auto w-full mt-6">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              How to use the badge reader
            </h3>
            <ol className="space-y-2 text-blue-800">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-blue-200 flex items-center justify-center flex-shrink-0 text-sm font-bold">1</span>
                <span>Locate the badge reader on the <strong>left side</strong> of this kiosk</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-blue-200 flex items-center justify-center flex-shrink-0 text-sm font-bold">2</span>
                <span>Insert your Blue Badge <strong>flat and horizontally</strong> into the letterbox slot</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-blue-200 flex items-center justify-center flex-shrink-0 text-sm font-bold">3</span>
                <span>The <strong>top and bottom cameras</strong> will automatically capture your badge</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-blue-200 flex items-center justify-center flex-shrink-0 text-sm font-bold">4</span>
                <span>Hold still during the <strong>5-second countdown</strong></span>
              </li>
            </ol>
          </div>
        </div>
      </main>

      {/* Footer Navigation */}
      <footer className="px-6 py-6 border-t-2 border-slate-200 bg-white/80 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate("/kiosk/form", { 
              state: { 
                category: { 
                  id: "disabled_blue_badge", 
                  name: "Disabled Blue Badge", 
                  requiresOCR: true,
                  speechName: "Disabled Blue Badge"
                } 
              } 
            })}
            className="kiosk-btn-secondary flex items-center gap-3"
            data-testid="back-button"
          >
            <ArrowLeft className="w-6 h-6" />
            <span>Back</span>
          </button>

          <button
            onClick={handleContinue}
            className="kiosk-btn-primary flex items-center gap-3"
            data-testid="continue-button"
          >
            <span>Ready to Scan</span>
            <ArrowRight className="w-6 h-6" />
          </button>
        </div>
      </footer>
    </div>
  );
}
